import { AlertDialog, Badge, Button, lucideIcons, toast } from '@round/creatorbase-ui';
import { useCallback, useState } from 'react';
import { ClientInfluencerAccount } from './helpers';
import CreatorMergeModal from '../CreatorMergeModal/CreatorMergeModal';
import { useClientInfluencer, useUnmergeClientInfluencerAccounts } from '@round/creatorbase-helpers/api-hooks';
import { creatorbaseQueries } from '@round/api';

const { Link2, Unlink2 } = lucideIcons;

type Props = {
    accountCount: number;
    activeAccount: ClientInfluencerAccount | undefined;
    clientInfluencerId: string | undefined;
    className?: string;
};

const AccountLinkButton = ({ accountCount, activeAccount, clientInfluencerId, className }: Props) => {
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
    const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);

    const { data: clientInfluencer, isLoading } = useClientInfluencer(clientInfluencerId);
    const { mutate: unmerge, isPending: isUnlinking } = useUnmergeClientInfluencerAccounts({
        onSuccess: () => {
            setIsUnlinkConfirmOpen(false);
            toast.success('Accounts unlinked successfully.');
        },
        onError: (error) => {
            setIsUnlinkConfirmOpen(false);
            toast.error(creatorbaseQueries.clientInfluencerAccounts.utils.unmerge.getErrorMessage(error));
        },
    });
    const isUnlinkReady = !isLoading && !!clientInfluencer;

    const handleMergeComplete = useCallback(() => {
        setIsMergeModalOpen(false);
    }, []);

    const handleUnlinkConfirm = useCallback(() => {
        if (!clientInfluencer || !clientInfluencerId) return;
        unmerge({
            client_id: clientInfluencer.client_id,
            unmerge_client_influencer_id: clientInfluencerId,
        });
    }, [unmerge, clientInfluencer, clientInfluencerId]);

    return (
        <>
            {accountCount > 1 && (
                <AlertDialog open={isUnlinkConfirmOpen} onOpenChange={setIsUnlinkConfirmOpen}>
                    <AlertDialog.Trigger
                        render={
                            <Button asChild variant="outline" size="xs" className={className} disabled={!isUnlinkReady}>
                                <Badge size="sm" variant="outline">
                                    <Unlink2 />
                                    Unlink
                                </Badge>
                            </Button>
                        }
                    />
                    <AlertDialog.Content size="sm">
                        <AlertDialog.Header>
                            <AlertDialog.Title>Unlink accounts?</AlertDialog.Title>
                            <AlertDialog.Description>
                                This will separate the linked platform accounts into individual creators.
                            </AlertDialog.Description>
                        </AlertDialog.Header>
                        <AlertDialog.Footer>
                            <AlertDialog.Cancel variant="outline" size="sm">
                                Cancel
                            </AlertDialog.Cancel>
                            <AlertDialog.Action
                                variant="destructive"
                                size="sm"
                                onClick={handleUnlinkConfirm}
                                disabled={isUnlinking}
                            >
                                {isUnlinking ? 'Unlinking...' : 'Unlink'}
                            </AlertDialog.Action>
                        </AlertDialog.Footer>
                    </AlertDialog.Content>
                </AlertDialog>
            )}

            {accountCount === 1 && (
                <Button
                    asChild
                    variant="default"
                    size="xs"
                    className={className}
                    onClick={() => setIsMergeModalOpen(true)}
                    disabled={!activeAccount || !clientInfluencerId}
                >
                    <Badge size="sm">
                        <Link2 />
                        Link
                    </Badge>
                </Button>
            )}

            {activeAccount && (
                <CreatorMergeModal
                    isOpen={isMergeModalOpen}
                    closeModal={() => setIsMergeModalOpen(false)}
                    sourceCreatorData={
                        clientInfluencerId
                            ? {
                                  clientInfluencerId,
                                  fullName: activeAccount.full_name,
                                  platformUsername: activeAccount.platform_username,
                                  userId: activeAccount.creatorbase_user_id,
                              }
                            : null
                    }
                    onMergeComplete={handleMergeComplete}
                    sourcePlatform={activeAccount.platform}
                />
            )}
        </>
    );
};

export default AccountLinkButton;
